import { Routes } from '@angular/router';

// Layout
import { AppLayoutComponent } from './layout/app.layout.component';

// Guards
import { AdminGuardService } from './common/guards/auth/admin-guard.service';
import { AuthGuardService } from './common';
import { canMatchPermission } from './common/guards/permission.guard';
import { canActivatePermission } from './common/guards/permission.guard';
import { planGuard, PlanFeature } from './guards/plan.guard';
import { ProjectGuardService } from './common/guards/project/project.guard';

// Resolvers
import { BillingProductResolver } from './pages/billing/billing-product-resolver';

// Components - Shared/Errors
import { AccessDeniedComponent } from './components/shared/access-denied/access-denied.component';
import { App404Component } from './components/shared/404/404.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppLoginComponent } from './pages/app.login.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';

// Components - Pages
import { AcceptInviteComponent } from './pages/organization-settings/accept-invite/accept-invite.component';
import { AdminpanelComponent } from './pages/adminpanel/adminpanel.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DigitalBusinessCardLandingComponent } from './pages/digital-business-card/component/digital-business-card-landing/digital-business-card-landing.component';
import { OrganizationSubscriptionInactiveComponent } from './pages/organization-subscription-inactive/organization-subscription-inactive.component';
import { SeoUsageComponent } from './pages/seo/seo-usage/seo-usage.component';

// Configs
import { ADMIN_ROUTES } from './pages/adminpanel/config';

export const AppRoutes: Routes = [
    {
        path: 'account',
        loadChildren: () =>
            import('./pages/account/account.routing').then(m => m.SessionRoutes),
        component: AppLayoutComponent
    },
    {
        path: 'billing',
        loadChildren: () =>
            import('./pages/billing/billing-routing.module').then(m => m.BillingRoutes),
        component: AppLayoutComponent,
        canActivate: [canMatchPermission('Org.Billing.Access')],
    },
    {
        path: 'seo',
        loadChildren: () =>
            import('./pages/seo/seo-routing.module').then(m => m.KeywordSearchRoutes),
        component: AppLayoutComponent,
        canActivate: [
            AuthGuardService,
            () => planGuard(PlanFeature.SEO)()
        ],
        resolve: {
            billingProduct: BillingProductResolver
        },
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'settings',
        loadChildren: () =>
            import('./pages/settings/settings-routing.module').then(m => m.SettingsRoutes),
        component: AppLayoutComponent,
        resolve: {
            billingProduct: BillingProductResolver
        },
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'sentiment',
        loadChildren: () =>
            import('./pages/sentiment/sentiment-routing.module').then(m => m.SentimentRoutes),
        component: AppLayoutComponent,
        canActivate: [AuthGuardService],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'askkevin',
        loadChildren: () =>
            import('./pages/kevinai/kevinai-routing.module').then(m => m.AnswersRoutes),
        component: AppLayoutComponent,
        canActivate: [
            AuthGuardService,
            () => planGuard(PlanFeature.KEVIN_AI)()
        ],
        runGuardsAndResolvers: 'always',
        resolve: {
            billingProduct: BillingProductResolver
        },
    },
    {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
    },
    {
        path: 'dashboard',
        component: AppLayoutComponent,
        canActivate: [
            AuthGuardService,
            () => planGuard(PlanFeature.SEO)()
        ],
        resolve: {
            billingProduct: BillingProductResolver
        },
        children: [
            {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
            },
            {
                path: 'overview',
                component: DashboardComponent,
                data: { dashboardSection: 'dashboard' }
            },
            {
                path: 'domain-research',
                loadComponent: () => import('./pages/seo/domain-research/domain-research.component').then(m => m.DomainResearchComponent),
                canActivate: [ProjectGuardService],
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        loadComponent: () => import('./pages/seo/domain-research/domain-research-overview.component').then(m => m.DomainResearchOverviewComponent)
                    },
                    {
                        path: 'keywords',
                        loadComponent: () => import('./pages/seo/domain-research/keyword-research.component').then(m => m.KeywordResearchComponent),
                        canActivate: [canActivatePermission('Org.KeywordResearch.View')]
                    },
                    {
                        path: 'keywords/:keyword',
                        loadComponent: () => import('./pages/seo/domain-research/keyword-detail.component').then(m => m.KeywordDetailComponent),
                        canActivate: [canActivatePermission('Org.KeywordResearch.View')]
                    },
                    {
                        path: 'competitors',
                        loadComponent: () => import('./pages/seo/domain-research/competitor-research.component').then(m => m.CompetitorResearchWorkspaceComponent),
                        canActivate: [canActivatePermission('Org.Competitors.View')]
                    },
                    {
                        path: 'competitors/:domain',
                        loadComponent: () => import('./pages/seo/domain-research/competitor-detail.component').then(m => m.CompetitorDetailComponent),
                        canActivate: [canActivatePermission('Org.Competitors.View')]
                    },
                    {
                        path: 'backlinks',
                        loadComponent: () => import('./pages/seo/domain-research/backlink-research.component').then(m => m.BacklinkResearchComponent)
                    },
                    {
                        path: 'backlinks/:domain',
                        loadComponent: () => import('./pages/seo/domain-research/referring-domain-detail.component').then(m => m.ReferringDomainDetailComponent)
                    },
                    {
                        path: 'content-ideas',
                        loadComponent: () => import('./pages/seo/domain-research/content-opportunities.component').then(m => m.ContentOpportunitiesComponent),
                        canActivate: [canActivatePermission('Org.KeywordResearch.View'), canActivatePermission('Org.Competitors.View')]
                    }
                ]
            },
            {
                path: 'analytics',
                component: DashboardComponent,
                data: { dashboardSection: 'ga-kpi' }
            },
            {
                path: 'opportunities',
                component: DashboardComponent,
                data: { dashboardSection: 'opportunities' }
            }
        ]
    },
    {
        path: 'admin/panel',
        component: AppLayoutComponent,
        canActivate: [AdminGuardService],
        children: [
            {
                path: '',
                component: AdminpanelComponent,
                data: { page: 'admin-panel', breadcrumb: 'Admin Panel' },
                children: ADMIN_ROUTES
            }
        ]
    },
    {
        path: 'contacts',
        loadChildren: () =>
            import('./pages/contacts/module/contacts-routing.module').then(m => m.ContactsRoutes),
        component: AppLayoutComponent,
        canActivate: [AuthGuardService, ProjectGuardService]
    },
    {
        path: 'dbc',
        loadChildren: () =>
            import('./pages/digital-business-card/digital-business-card-routing.module').then(m => m.DigitalBusinessCardRoutes),
        component: AppLayoutComponent,
        canActivate: [
            AuthGuardService,
            () => planGuard(PlanFeature.BUSINESS_CARD)()
        ],
        runGuardsAndResolvers: 'always',
        resolve: {
            billingProduct: BillingProductResolver
        },
    },
    {
        path: 'dbc/:username',
        component: DigitalBusinessCardLandingComponent
    },
    {
        path: 'org/settings',
        loadChildren: () =>
            import('./pages/organization-settings/organization-settings-routing').then(m => m.OrganizationRoutes),
        component: AppLayoutComponent,
        canActivate: [AuthGuardService],
    },
    {
        path: 'organization/accept',
        // canActivate: [AuthGuardService],
        component: AcceptInviteComponent
    },
    {
        path: 'organization/subscription-inactive',
        component: OrganizationSubscriptionInactiveComponent
    },
    {
        path: 'organizations/:orgId/seo-usage',
        component: AppLayoutComponent,
        canActivate: [AuthGuardService],
        children: [
            {
                path: '',
                component: SeoUsageComponent,
                canActivate: [canActivatePermission('Org.Audit.View')],
                data: { page: 'seo-usage', breadcrumb: 'SEO Usage' }
            }
        ]
    },

    // Authenticated redirect for legacy URLs or post-actions (e.g. after accepting invite)
    {
        path: 'manage-redirect',
        canActivate: [AuthGuardService],
        component: App404Component
    },
    {
        path: 'access-denied',
        canActivate: [AuthGuardService],
        component: AccessDeniedComponent
    },

    // {
    //     path: '',
    //     component: AppMainComponent,
    //     children: [
    //        // {path: '', component: AppMainComponent},

    //         {path: 'pages/help', component: AppHelpComponent},
    //     ]
    // },

    // {path: 'error', component: AppErrorComponent},
    // {path: 'access', component: AppAccessdeniedComponent},
    // {path: 'notfound', component: AppNotfoundComponent},
    // {path: 'login', component: AppLoginComponent},

    {path: '**', redirectTo: '/notfound'},
];
